/**
 * LRN（Land Run）の風層アドバイザー。
 *
 * パイロットからの要望（docs/08 §67 の5件目）:
 *
 * > LRNで最適に使うべき風の層、それぞれのマーク時刻、上昇/降下タイミング
 *
 * `planLrnTime`/`planLrnCircle` は「最良の1案」しか返さないため、
 * 「なぜその層なのか」「他にどの層が有力か」が分からず、風が変わったときに
 * 次善策へ切り替えられなかった。ここでは同じ探索を層ペア単位で回し、
 *
 *   1. **行動の時系列**（何分後に何をするか）
 *   2. **候補のランキング**（採用しなかった層の組み合わせも評点つきで）
 *
 * の2つを返す。探索そのものは `lrn.ts` の `planLrnTimePair`/
 * `planLrnCirclePair` を呼ぶだけで、ロジックを二重に持たない。
 *
 * ★★ ここで出す数字は**飛行支援のための参考値**であって公式スコアでは
 *   ない（`lrn.ts`・`three-dt-strategy.ts` と同じ方針）。
 */

import {
  layersInBand,
  lrnLayerPairScore,
  orderedLayerPairs,
  planLrnCirclePair,
  planLrnTimePair,
  type LrnPlan,
  type LrnPlanOptions,
  type LrnPoint,
  type LrnTask,
  type LrnWindLayer,
} from './lrn.ts';

/** 行動の時系列の1項目 */
export type LrnAdviceStep =
  | {
      kind: 'mark';
      /** 'A' | 'B' | 'C' */
      label: string;
      sec: number;
      point: LrnPoint;
      altFt: number;
    }
  | {
      kind: 'ride';
      fromSec: number;
      toSec: number;
      layer: LrnWindLayer;
    }
  | {
      kind: 'climb' | 'descend';
      fromSec: number;
      toSec: number;
      fromAltFt: number;
      toAltFt: number;
    };

/** 層ペア1組の評価。ランキング表示に使う */
export interface LrnLayerPairCandidate {
  layerA: LrnWindLayer;
  layerB: LrnWindLayer;
  /**
   * `lrnLayerPairScore` の値（速度の積×向きの差のsin）。
   * ★ 実際の面積とは**別物**。あくまで「大きな三角形を作りやすそうか」の
   *   粗い目安で、円モードでは円からの脱出位置に強く左右される。
   *   並べ替えの基準には使わず、参考として併記するだけにしてある
   */
  pairScore: number;
  /** 実際にシミュレートした推定面積 [km²]。**並べ替えはこちらで行う** */
  areaKm2: number;
  /** 高度移行にかかる時間 [秒] */
  transitionSec: number;
  plan: LrnPlan;
}

export interface LrnAdvice {
  /** 最良案（candidates[0].plan と同じ）。成立する案が無ければ null */
  best: LrnPlan | null;
  /** 面積の大きい順。既定で上位5件 */
  candidates: LrnLayerPairCandidate[];
  /** 最良案を実行するための行動の時系列。best が null なら空 */
  steps: LrnAdviceStep[];
  /** 高度帯に入っていた層の数（0や1なら、そもそも2層を使えない） */
  usableLayerCount: number;
}

export interface LrnAdviceOptions extends LrnPlanOptions {
  /** ランキングに残す件数。既定5 */
  maxCandidates?: number;
}

const DEFAULT_MAX_CANDIDATES = 5;

/**
 * 最良案から行動の時系列を組み立てる。
 *
 * LRNは「A点でマーク → 層Aに乗る → B点でマーク → 高度移行 → 層Bに乗る →
 * C点でマーク」という流れ。`LrnPlan` は既に脚と時刻を持っているので、
 * ここでは**人が読んで動ける粒度に開くだけ**で、新しい計算はしない。
 *
 * ★ 高度移行は第1脚の終わり（B点）から第2脚の始まりまでの区間。
 *   マークBは移行の**前**（＝第1脚の終点）に打つ。移行中も流されるため、
 *   移行後の位置は第2脚の起点であってB点ではない。
 */
export function buildLrnAdviceSteps(plan: LrnPlan): LrnAdviceStep[] {
  const [leg1, leg2] = plan.legs;
  const steps: LrnAdviceStep[] = [];

  steps.push({ kind: 'mark', label: 'A', sec: leg1.fromSec, point: leg1.from, altFt: leg1.layer.altFt });
  steps.push({ kind: 'ride', fromSec: leg1.fromSec, toSec: leg1.toSec, layer: leg1.layer });
  steps.push({ kind: 'mark', label: 'B', sec: leg1.toSec, point: leg1.to, altFt: leg1.layer.altFt });

  const transitionSec = leg2.fromSec - leg1.toSec;
  if (transitionSec > 0) {
    steps.push({
      kind: leg2.layer.altFt >= leg1.layer.altFt ? 'climb' : 'descend',
      fromSec: leg1.toSec,
      toSec: leg2.fromSec,
      fromAltFt: leg1.layer.altFt,
      toAltFt: leg2.layer.altFt,
    });
  }

  steps.push({ kind: 'ride', fromSec: leg2.fromSec, toSec: leg2.toSec, layer: leg2.layer });
  steps.push({ kind: 'mark', label: 'C', sec: leg2.toSec, point: leg2.to, altFt: leg2.layer.altFt });

  return steps;
}

/**
 * LRNの層選定を助言する。
 *
 * `planLrnTime`/`planLrnCircle` と**同じ探索**を層ペア単位で回し、成立した
 * 案をすべて集めて面積の大きい順に並べる。最良案は既存プランナーの結果と
 * 一致する（同じ関数を呼んでいるため）。
 */
export function adviseLrn(
  task: LrnTask,
  profile: readonly LrnWindLayer[],
  options: LrnAdviceOptions = {},
): LrnAdvice {
  const layers = layersInBand(profile, task.altMinFt, task.altMaxFt);
  const maxCandidates = options.maxCandidates ?? DEFAULT_MAX_CANDIDATES;

  const candidates: LrnLayerPairCandidate[] = [];
  for (const [a, b] of orderedLayerPairs(layers)) {
    const plan =
      task.mode === 'time' ? planLrnTimePair(task, a, b) : planLrnCirclePair(task, a, b, options);
    if (!plan) continue;
    candidates.push({
      layerA: a,
      layerB: b,
      pairScore: lrnLayerPairScore(a, b),
      areaKm2: plan.areaKm2,
      transitionSec: plan.legs[1].fromSec - plan.legs[0].toSec,
      plan,
    });
  }

  candidates.sort((x, y) => y.areaKm2 - x.areaKm2);
  const top = candidates.slice(0, maxCandidates);
  const best = top[0]?.plan ?? null;

  return {
    best,
    candidates: top,
    steps: best ? buildLrnAdviceSteps(best) : [],
    usableLayerCount: layers.length,
  };
}
